import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Trans } from 'react-i18next';
import { ReactNode } from 'react';
import ComingSoonBanner from '../components/ui/ComingSoonBanner';

interface PressRelease {
  id: string;
  title: ReactNode;
  summary: string;
  publishDate: string;
  category: string;
  isFeatured?: boolean;
}

interface MediaResource {
  id: string;
  title: ReactNode;
  description: ReactNode;
  type: 'logo' | 'image' | 'video' | 'document';
  downloadUrl: string;
  fileSize: string;
}

const PressPage: React.FC = () => {
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState('all');

  const pressReleases: PressRelease[] = [
    {
      id: '1',
      title: (
        <Trans i18nKey="press.releases.release1.title">
          <span className="font-brand">tvia.ai</span> Launches Revolutionary AI-Powered Legal Platform
        </Trans>
      ),
      summary: t('press.releases.release1.summary', 'The company introduces a groundbreaking platform that makes legal services accessible to everyone through artificial intelligence.'),
      publishDate: '2024-01-15',
      category: 'product-launch',
      isFeatured: true
    },
    {
      id: '2',
      title: (
        <Trans i18nKey="press.releases.release2.title">
          <span className="font-brand">tvia.ai</span> Secures $10M Series A Funding Round
        </Trans>
      ),
      summary: t('press.releases.release2.summary', 'Investment will accelerate product development and expand market reach for AI-powered legal services.'),
      publishDate: '2024-01-10',
      category: 'funding'
    },
    {
      id: '3',
      title: (
        <Trans i18nKey="press.releases.release3.title">
          <span className="font-brand">tvia.ai</span> Partners with Leading Law Firms
        </Trans>
      ),
      summary: t('press.releases.release3.summary', 'Strategic partnerships established to enhance legal document accuracy and provide expert consultation services.'),
      publishDate: '2024-01-05',
      category: 'partnerships'
    },
    {
      id: '4',
      title: (
        <Trans i18nKey="press.releases.release4.title">
          <span className="font-brand">tvia.ai</span> Named Top Legal Tech Startup of 2024
        </Trans>
      ),
      summary: t('press.releases.release4.summary', 'Recognition from industry leaders for innovation in democratizing access to legal services.'),
      publishDate: '2024-01-01',
      category: 'awards'
    },
    {
      id: '5',
      title: (
        <Trans i18nKey="press.releases.release5.title">
          <span className="font-brand">tvia.ai</span> Expands to European Markets
        </Trans>
      ),
      summary: t('press.releases.release5.summary', 'Platform now available in multiple European countries with localized legal document templates.'),
      publishDate: '2023-12-20',
      category: 'expansion'
    },
    {
      id: '6',
      title: (
        <Trans i18nKey="press.releases.release6.title">
          <span className="font-brand">tvia.ai</span> Introduces Advanced Class Action Tools
        </Trans>
      ),
      summary: t('press.releases.release6.summary', 'New features help users identify and join relevant class action lawsuits with ease.'),
      publishDate: '2023-12-15',
      category: 'product-update'
    }
  ];

  const mediaResources: MediaResource[] = [
    {
      id: '1',
      title: (
        <Trans i18nKey="press.media.logo.title">
          <span className="font-brand">tvia.ai</span> Logo (PNG)
        </Trans>
      ),
      description: t('press.media.logo.description', 'High-resolution logo in PNG format with transparent background'),
      type: 'logo',
      downloadUrl: '/media/tvia-logo.png',
      fileSize: '2.1 MB'
    },
    {
      id: '2',
      title: (
        <Trans i18nKey="press.media.logoVector.title">
          <span className="font-brand">tvia.ai</span> Logo (SVG)
        </Trans>
      ),
      description: t('press.media.logoVector.description', 'Vector logo in SVG format for scalable use'),
      type: 'logo',
      downloadUrl: '/media/tvia-logo.svg',
      fileSize: '45 KB'
    },
    {
      id: '3',
      title: t('press.media.teamPhoto.title', 'Executive Team Photo'),
      description: (
        <Trans i18nKey="press.media.teamPhoto.description">
          High-quality photo of the <span className="font-brand">tvia.ai</span> executive team
        </Trans>
      ),
      type: 'image',
      downloadUrl: '/media/executive-team.jpg',
      fileSize: '8.5 MB'
    },
    {
      id: '4',
      title: t('press.media.productScreenshot.title', 'Platform Screenshots'),
      description: (
        <Trans i18nKey="press.media.productScreenshot.description">
          Screenshots of the <span className="font-brand">tvia.ai</span> platform interface
        </Trans>
      ),
      type: 'image',
      downloadUrl: '/media/platform-screenshots.zip',
      fileSize: '15.2 MB'
    },
    {
      id: '5',
      title: t('press.media.companyFactSheet.title', 'Company Fact Sheet'),
      description: t('press.media.companyFactSheet.description', 'Comprehensive fact sheet with company information and statistics'),
      type: 'document',
      downloadUrl: '/media/company-fact-sheet.pdf',
      fileSize: '1.8 MB'
    },
    {
      id: '6',
      title: t('press.media.productDemo.title', 'Product Demo Video'),
      description: (
        <Trans i18nKey="press.media.productDemo.description">
          Video demonstration of the <span className="font-brand">tvia.ai</span> platform features
        </Trans>
      ),
      type: 'video',
      downloadUrl: '/media/product-demo.mp4',
      fileSize: '45.7 MB'
    }
  ];

  const categories = [
    { id: 'all', name: t('press.categories.all', 'All Releases') },
    { id: 'product-launch', name: t('press.categories.productLaunch', 'Product Launches') },
    { id: 'funding', name: t('press.categories.funding', 'Funding') },
    { id: 'partnerships', name: t('press.categories.partnerships', 'Partnerships') },
    { id: 'awards', name: t('press.categories.awards', 'Awards & Recognition') },
    { id: 'expansion', name: t('press.categories.expansion', 'Expansion') },
    { id: 'product-update', name: t('press.categories.productUpdate', 'Product Updates') }
  ];

  const filteredReleases = pressReleases.filter(release => 
    selectedCategory === 'all' || release.category === selectedCategory
  );

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getResourceIcon = (type: string) => {
    switch (type) {
      case 'logo': return '🎨';
      case 'image': return '📷';
      case 'video': return '🎥';
      case 'document': return '📄';
      default: return '📁';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {t('press.title', 'Press & Media')}
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('press.subtitle', 'Latest news, press releases, and media resources for journalists and media professionals.')}
          </p>
        </div>

        {/* Coming Soon Banner */}
        <ComingSoonBanner />

        {/* Contact Information */}
        <div className="bg-gradient-to-r from-cyan-600 to-blue-600 rounded-lg p-8 text-center text-white mb-12">
          <h2 className="text-2xl font-bold mb-4">
            {t('press.mediaContact', 'Media Contact')}
          </h2>
          <p className="text-lg mb-6 opacity-90">
            {t('press.mediaContactDescription', 'For press inquiries, interviews, or media requests, please contact our communications team.')}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-2xl mx-auto">
            <div>
              <p className="font-medium">{t('press.email', 'Email')}</p>
              <p className="text-sm opacity-90">press@tvia.ai</p>
            </div>
            <div>
              <p className="font-medium">{t('press.phone', 'Phone')}</p>
              <p className="text-sm opacity-90">+1 (555) 123-4567</p>
            </div>
            <div>
              <p className="font-medium">{t('press.responseTime', 'Response Time')}</p>
              <p className="text-sm opacity-90">{t('press.within24Hours', 'Within 24 hours')}</p>
            </div>
          </div>
        </div>

        {/* Press Releases */}
        <div className="mb-16">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-3xl font-bold text-gray-900">
              {t('press.pressReleases', 'Press Releases')}
            </h2>
            <div className="flex space-x-2">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                    selectedCategory === category.id
                      ? 'bg-cyan-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-6">
            {filteredReleases.map((release) => (
              <div key={release.id} className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      {release.isFeatured && (
                        <span className="bg-yellow-100 text-yellow-800 text-xs font-medium px-2 py-1 rounded-full mr-3">
                          {t('press.featured', 'Featured')}
                        </span>
                      )}
                      <span className="bg-gray-100 text-gray-800 text-xs font-medium px-2 py-1 rounded-full">
                        {categories.find(cat => cat.id === release.category)?.name}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      {release.title}
                    </h3>
                    <p className="text-gray-600 mb-4">
                      {release.summary}
                    </p>
                    <div className="flex items-center text-sm text-gray-500">
                      <span>{formatDate(release.publishDate)}</span>
                    </div>
                  </div>
                  <div className="ml-6">
                    <Link
                      to={`/press/release/${release.id}`}
                      className="bg-cyan-600 text-white px-4 py-2 rounded-md hover:bg-cyan-700 transition-colors duration-200"
                    >
                      {t('press.readMore', 'Read More')}
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Media Resources */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">
            {t('press.mediaResources', 'Media Resources')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {mediaResources.map((resource) => (
              <div key={resource.id} className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center mb-4">
                  <span className="text-2xl mr-3">{getResourceIcon(resource.type)}</span>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {resource.title}
                    </h3>
                    <p className="text-sm text-gray-500">{resource.fileSize}</p>
                  </div>
                </div>
                <p className="text-gray-600 text-sm mb-4">
                  {resource.description}
                </p>
                <a
                  href={resource.downloadUrl}
                  download
                  className="inline-flex items-center text-cyan-600 hover:text-cyan-700 text-sm font-medium"
                >
                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                  {t('press.download', 'Download')}
                </a>
              </div>
            ))}
          </div>
        </div>

        {/* Company Information */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          {/* About tvia.ai */}
          <div className="bg-white rounded-lg shadow-sm p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              <Trans i18nKey="press.aboutTvia">
                About <span className="font-brand">tvia.ai</span>
              </Trans>
            </h2>
            <div className="space-y-4 text-gray-700">
              <p>
                <Trans i18nKey="press.aboutDescription1">
                  <span className="font-brand">tvia.ai</span> is a revolutionary legal technology platform that democratizes access to legal services through artificial intelligence. Our mission is to make legal processes more accessible, affordable, and efficient for everyone.
                </Trans>
              </p>
              <p>
                {t('press.aboutDescription2', 'Founded in 2023, we have quickly become a leader in legal tech innovation, helping thousands of individuals and businesses navigate complex legal processes with confidence.')}
              </p>
              <div className="grid grid-cols-2 gap-4 mt-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-cyan-600">10K+</div>
                  <div className="text-sm text-gray-600">{t('press.users', 'Users')}</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-cyan-600">50+</div>
                  <div className="text-sm text-gray-600">{t('press.countries', 'Countries')}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Leadership Team */}
          <div className="bg-white rounded-lg shadow-sm p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              {t('press.leadership', 'Leadership Team')}
            </h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-gray-900">Sarah Johnson</h3>
                <p className="text-sm text-gray-600">{t('press.ceo', 'Chief Executive Officer')}</p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Michael Chen</h3>
                <p className="text-sm text-gray-600">{t('press.cto', 'Chief Technology Officer')}</p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Emily Rodriguez</h3>
                <p className="text-sm text-gray-600">{t('press.cpo', 'Chief Product Officer')}</p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">David Thompson</h3>
                <p className="text-sm text-gray-600">{t('press.clo', 'Chief Legal Officer')}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Facts */}
        <div className="bg-white rounded-lg shadow-sm p-8 mb-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            {t('press.quickFacts', 'Quick Facts')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-cyan-600 mb-2">2023</div>
              <div className="text-gray-600">{t('press.founded', 'Founded')}</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-cyan-600 mb-2">$10M</div>
              <div className="text-gray-600">{t('press.funding', 'Series A Funding')}</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-cyan-600 mb-2">50+</div>
              <div className="text-gray-600">{t('press.employees', 'Employees')}</div>
            </div>
          </div>
        </div>

        {/* Contact CTA */}
        <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-lg p-8 text-center text-white">
          <h2 className="text-2xl font-bold mb-4">
            {t('press.getInTouch', 'Get in Touch')}
          </h2>
          <p className="text-lg mb-6 opacity-90">
            <Trans i18nKey="press.contactDescription">
              Ready to write about <span className="font-brand">tvia.ai</span>? We'd love to hear from you and provide any additional information you need.
            </Trans>
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="mailto:press@tvia.ai"
              className="bg-white text-gray-800 px-6 py-3 rounded-md font-medium hover:bg-gray-100 transition-colors duration-200"
            >
              {t('press.emailUs', 'Email Us')}
            </a>
            <Link
              to="/contact"
              className="border-2 border-white text-white px-6 py-3 rounded-md font-medium hover:bg-white hover:text-gray-800 transition-colors duration-200"
            >
              {t('press.contactForm', 'Contact Form')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PressPage;